import {
  Banknote,
  BookOpenCheck,
  BriefcaseBusiness,
  Building,
  Coins,
  FilePlus2,
  Gauge,
  GraduationCap,
  Images,
  Activity,
  LayoutDashboard,
  Landmark,
  LibraryBig,
  ListChecks,
  Map,
  Network,
  PackageCheck,
  Pickaxe,
  Repeat2,
  Route,
  Search,
  Settings,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Tags,
  Ticket,
  UploadCloud,
  WalletCards,
  UsersRound,
} from 'lucide-react';

const nuclei = [
  {
    id: 'overview',
    label: 'Overview',
    shortLabel: 'Home',
    routeBase: '/dashboard',
    match: ['/', '/dashboard'],
    icon: Gauge,
    tone: 'core',
    summary: 'Unified ecosystem operating view',
    scope: 'protocol',
    maturity: 'prototype',
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    routeBase: '/portfolio',
    icon: Map,
    tone: 'core',
    summary: 'Read-only ecosystem maturity, dependency and authority overview',
    scope: 'protocol',
    maturity: 'D3',
    sections: [
      { to: '/portfolio', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/portfolio/dependencies', label: 'Dependencies', icon: Network },
      { to: '/portfolio/opportunities', label: 'Opportunities', icon: Search },
      { to: '/portfolio/authority', label: 'Authority', icon: ShieldCheck },
      { to: '/portfolio/Business', label: 'Nucleus Detail', icon: LibraryBig },
    ],
  },
  {
    id: 'mining',
    label: 'Mining',
    routeBase: '/mining',
    icon: Pickaxe,
    tone: 'infrastructure',
    summary: 'Tokenized mining exposure, provider risk and treasury allocation readiness',
    scope: 'operator',
    maturity: 'prototype',
    sections: [
      { to: '/mining', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/mining/providers', label: 'Providers', icon: Pickaxe },
      { to: '/mining/hash-tokens', label: 'Hash Tokens', icon: LibraryBig },
      { to: '/mining/vaults', label: 'Vaults', icon: WalletCards },
      { to: '/mining/allocations', label: 'Allocations', icon: SlidersHorizontal },
      { to: '/mining/telemetry', label: 'Telemetry', icon: Activity },
      { to: '/mining/treasury', label: 'Treasury', icon: Landmark },
      { to: '/mining/accounting', label: 'Accounting', icon: LibraryBig },
      { to: '/mining/reconciliation', label: 'Reconciliation', icon: ListChecks },
      { to: '/mining/risk', label: 'Risk', icon: ShieldCheck },
      { to: '/mining/governance', label: 'Governance', icon: ShieldCheck },
      { to: '/mining/actions', label: 'Actions', icon: ListChecks },
      { to: '/mining/reports', label: 'Reports', icon: LibraryBig },
    ],
  },
  {
    id: 'defi',
    label: 'Treasury & Defi',
    shortLabel: 'Defi',
    routeBase: '/defi',
    icon: Banknote,
    tone: 'treasury',
    summary: 'Treasury, vault and protocol finance surfaces',
    scope: 'protocol',
    maturity: 'mock',
    sections: [
      { to: '/defi', label: 'Dashboard', icon: LayoutDashboard, end: true },
    ],
  },
  {
    id: 'acs',
    label: 'ACS',
    routeBase: '/acs',
    match: ['/acs', '/mcps'],
    icon: Network,
    tone: 'infrastructure',
    summary: 'Operational intelligence, tenant services and policy visibility',
    scope: 'operator',
    maturity: 'prototype',
    sections: [
      { to: '/acs', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/acs/capabilities', label: 'Capabilities', icon: Network },
      { to: '/acs/services', label: 'Tenant Services', icon: UsersRound },
      { to: '/acs/products', label: 'Products', icon: PackageCheck },
      { to: '/acs/policy', label: 'Policy', icon: ShieldCheck },
      { to: '/acs/debug', label: 'Policy Debug', icon: SlidersHorizontal },
      { to: '/acs/status', label: 'Status', icon: Gauge },
      { to: '/acs/readiness', label: 'Readiness', icon: ListChecks },
      { to: '/mcps', label: 'Legacy Monitor', icon: Network },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    routeBase: '/governance',
    match: ['/governance', '/dao'],
    icon: Landmark,
    tone: 'constitutional',
    summary: 'DAO tenant operations and constitutional execution',
    scope: 'tenant',
    maturity: 'prototype',
    sections: [
      { to: '/governance', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/governance/tenants', label: 'DAO Tenants', icon: UsersRound },
      { to: '/governance/console?view=proposals', label: 'Proposals', icon: LibraryBig },
      { to: '/governance/console', label: 'Console', icon: Landmark },
      { to: '/governance?view=analytics', label: 'Analytics', icon: Gauge },
      { to: '/dao', label: 'DAO Context', icon: ShieldCheck },
    ],
    contextualFilterGroups: [
      {
        route: '/governance/tenants',
        label: 'Risk Level',
        param: 'risk',
        items: [
          { label: 'Low', value: 'low' },
          { label: 'Moderate', value: 'moderate' },
          { label: 'Balanced', value: 'balanced' },
          { label: 'High', value: 'high' },
          { label: 'Experimental', value: 'experimental' },
        ],
      },
      {
        route: '/governance/tenants',
        label: 'Investment Type',
        param: 'type',
        items: [
          { label: 'Mining', value: 'Mining' },
          { label: 'Trading', value: 'Trading' },
          { label: 'DeFi', value: 'DeFi' },
          { label: 'Staking', value: 'Staking' },
          { label: 'Yield', value: 'Yield' },
          { label: 'AI Infrastructure', value: 'AI Infrastructure' },
          { label: 'Compute', value: 'Compute' },
          { label: 'Marketplace', value: 'Marketplace' },
          { label: 'Education', value: 'Education' },
          { label: 'Treasury', value: 'Treasury' },
          { label: 'Venture', value: 'Venture' },
        ],
      },
      {
        route: '/governance/tenants',
        label: 'Chain',
        param: 'chain',
        items: [
          { label: 'Sepolia', value: 'Ethereum Sepolia' },
          { label: 'Base', value: 'Base' },
          { label: 'Arbitrum', value: 'Arbitrum' },
          { label: 'Polygon', value: 'Polygon' },
          { label: 'Harmony', value: 'Harmony Mainnet' },
        ],
      },
      {
        route: '/governance/tenants',
        label: 'Federation Tier',
        param: 'tier',
        items: [
          { label: 'Partner', value: 'partner' },
          { label: 'Sovereign', value: 'sovereign' },
          { label: 'Restricted', value: 'restricted' },
          { label: 'Observer', value: 'observer' },
        ],
      },
      {
        route: '/governance/tenants',
        label: 'Governance Status',
        param: 'status',
        items: [
          { label: 'Compliant', value: 'compliant' },
          { label: 'Under review', value: 'under-review' },
          { label: 'Restricted', value: 'restricted' },
          { label: 'Sanctioned', value: 'sanctioned' },
        ],
      },
      {
        route: '/governance/tenants',
        label: 'Featured',
        param: 'featured',
        items: [{ label: 'Featured Only', value: 'true' }],
      },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    routeBase: '/business',
    icon: BriefcaseBusiness,
    tone: 'enterprise',
    summary: 'Operational infrastructure, funding visibility and ACS runtime coordination',
    scope: 'operator',
    maturity: 'prototype',
    sections: [
      { to: '/business', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/business/intake', label: 'Intake', icon: FilePlus2 },
      { to: '/business/review-queue', label: 'Review Queue', icon: ListChecks },
      { to: '/business/projects', label: 'Projects', icon: LibraryBig },
      { to: '/business/assets', label: 'Assets', icon: PackageCheck },
      { to: '/business/registry', label: 'Registry', icon: Network },
      { to: '/business/workflows', label: 'Workflows', icon: Route },
      { to: '/business/events', label: 'Events', icon: Activity },
      { to: '/business/governance', label: 'Governance', icon: Landmark },
      { to: '/business/finance', label: 'Finance', icon: Banknote },
      { to: '/business/acs/readiness', label: 'ACS Readiness', icon: Network },
      { to: '/business/access', label: 'Access', icon: UsersRound },
      { to: '/business/runtime', label: 'Runtime', icon: ShieldCheck },
    ],
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    routeBase: '/marketplace',
    match: ['/marketplace', '/item'],
    icon: ShoppingCart,
    tone: 'commerce',
    summary: 'Product discovery, licenses and seller operations',
    scope: 'tenant',
    maturity: 'prototype',
    sections: [
      { to: '/marketplace', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/marketplace/dashboard', label: 'Dashboard', icon: LibraryBig },
      { to: '/marketplace/explore', label: 'Explore', icon: Tags },
      { to: '/marketplace/assets', label: 'Assets', icon: Images },
      { to: '/marketplace/listings', label: 'Listings', icon: ListChecks },
      { to: '/marketplace/create', label: 'Create/Sell', icon: FilePlus2 },
      { to: '/marketplace/orders', label: 'Orders / Billing', icon: PackageCheck },
      { to: '/marketplace/subscriptions', label: 'Subscriptions', icon: Repeat2 },
      { to: '/marketplace/treasury', label: 'Treasury Preview', icon: Landmark },
      { to: '/marketplace/governance', label: 'Governance', icon: ShieldCheck },
      { to: '/marketplace/licenses', label: 'Licenses', icon: WalletCards },
      { to: '/marketplace/publisher', label: 'Publisher Console', icon: UploadCloud },
    ],
    filterGroups: [
      {
        label: 'Categories',
        items: [
          { label: 'Education', to: '/marketplace/explore?category=Education' },
          { label: 'Governance', to: '/marketplace/explore?category=Governance' },
          { label: 'MCPs', to: '/marketplace/explore?category=MCPs' },
          { label: 'Trading', to: '/marketplace/explore?category=Trading' },
        ],
      },
      {
        label: 'Governance',
        items: [
          { label: 'Compliant', to: '/marketplace/explore?governanceStatus=compliant' },
          { label: 'Under review', to: '/marketplace/explore?governanceStatus=under-review' },
          { label: 'Restricted', to: '/marketplace/explore?governanceStatus=restricted' },
        ],
      },
      {
        label: 'Listing',
        items: [
          { label: 'Fixed price', to: '/marketplace/explore?listingType=fixed' },
          { label: 'Auction', to: '/marketplace/explore?listingType=auction' },
        ],
      },
    ],
  },
  {
    id: 'bba',
    label: 'BBA Agency',
    routeBase: '/bba',
    icon: Building,
    tone: 'agency',
    summary: 'Institutional brand, campaign and partnership operations',
    scope: 'tenant',
    maturity: 'prototype',
    sections: [
      { to: '/bba', label: 'Home', icon: LayoutDashboard, end: true },
      { to: '/bba/services', label: 'Services', icon: BriefcaseBusiness },
      { to: '/bba/portfolio', label: 'Portfolio', icon: LibraryBig },
      { to: '/bba/campaigns', label: 'Campaigns', icon: Tags },
      { to: '/bba/partnerships', label: 'Partnerships', icon: Network },
      { to: '/bba/governance', label: 'Governance', icon: ShieldCheck },
    ],
  },
  {
    id: 'academy',
    label: 'Academy',
    routeBase: '/academy',
    icon: GraduationCap,
    tone: 'learning',
    summary: 'Learning, credentials and reward progression',
    scope: 'user',
    maturity: 'prototype',
    sections: [
      { to: '/academy', label: 'Home', icon: GraduationCap, end: true },
      { to: '/academy/courses', label: 'Courses', icon: LibraryBig },
      { to: '/academy/workspace/constitutional-onboarding', label: 'Workspace', icon: BookOpenCheck },
      { to: '/academy/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/academy/progress', label: 'Progress Engine', icon: Gauge },
      { to: '/academy/eligibility', label: 'Eligibility', icon: ShieldCheck },
      { to: '/academy/certifications', label: 'Certifications', icon: ShieldCheck },
      { to: '/academy/rewards', label: 'Rewards', icon: Banknote },
      { to: '/academy/governance-review', label: 'Governance Review', icon: Landmark },
      { to: '/academy/paths/path-governance-operator', label: 'Learning Path', icon: Route },
    ],
    filterGroups: [
      {
        label: 'Categories',
        items: [
          { label: 'Governance', to: '/academy/courses?category=Governance' },
          { label: 'Treasury', to: '/academy/courses?category=Treasury' },
          { label: 'Marketplace', to: '/academy/courses?category=Marketplace' },
          { label: 'Proof of Knowledge', to: '/academy/courses?category=Proof%20of%20Knowledge' },
        ],
      },
      {
        label: 'Access',
        items: [
          { label: 'Free', to: '/academy/courses?accessType=free' },
          { label: 'Paid', to: '/academy/courses?accessType=paid' },
        ],
      },
      {
        label: 'Reward',
        items: [
          { label: 'Locked $NEURONS', to: '/academy/courses?rewardType=Locked%20%24NEURONS' },
          { label: 'Unlocked $NEURONS', to: '/academy/courses?rewardType=Unlocked%20%24NEURONS' },
        ],
      },
    ],
  },
  {
    id: 'dex',
    label: 'DEX',
    routeBase: '/dex',
    icon: Coins,
    tone: 'liquidity',
    summary: 'Liquidity, routing and token market access',
    scope: 'protocol',
    maturity: 'legacy-static',
    sections: [
      { to: '/dex', label: 'Swap', icon: Coins, end: true },
    ],
  },
  {
    id: 'lottery',
    label: 'Lottery',
    routeBase: '/lottery',
    icon: Ticket,
    tone: 'campaign',
    summary: 'Draws, prize pools and randomness visibility',
    scope: 'protocol',
    maturity: 'prototype',
    sections: [
      { to: '/lottery', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/lottery/draws', label: 'Draws', icon: Ticket },
      { to: '/lottery/game-models', label: 'Game models', icon: SlidersHorizontal },
      { to: '/lottery/tickets', label: 'Tickets', icon: WalletCards },
      { to: '/lottery/prizes', label: 'Prize pools', icon: Banknote },
      { to: '/lottery/governance', label: 'Governance', icon: ShieldCheck },
      { to: '/lottery/randomness', label: 'Randomness', icon: Network },
      { to: '/lottery/history', label: 'History', icon: LibraryBig },
    ],
  },
];

export const appShellNav = {
  primary: nuclei,
  secondary: [
    {
      id: 'settings',
      label: 'Settings',
      routeBase: '/settings',
      icon: Settings,
      tone: 'core',
      summary: 'Preferences, access and account settings',
      scope: 'user',
      maturity: 'mock',
    },
  ],
};

export const appShellGroups = [
  {
    id: 'protocol',
    label: 'Protocol',
    description: 'Global Axodus protocol and ecosystem surfaces',
    itemIds: ['overview', 'portfolio', 'governance', 'defi', 'dex', 'mining', 'lottery'],
  },
  {
    id: 'user',
    label: 'User',
    description: 'Wallet/account, learning and personal access surfaces',
    itemIds: ['academy', 'marketplace', 'settings'],
  },
  {
    id: 'tenant',
    label: 'Tenant',
    description: 'DAO, company and workspace operating surfaces',
    itemIds: ['business', 'bba'],
  },
  {
    id: 'operations',
    label: 'Operations',
    description: 'ACS, runtime safety, review and service observability',
    itemIds: ['acs'],
  },
];

export const shellSearchMeta = {
  icon: Search,
  placeholder: 'Search modules, DAOs, products...',
};

const allShellItems = [...appShellNav.primary, ...appShellNav.secondary];

function pathMatchesItem(pathname, item) {
  const matches = item.match ?? [item.routeBase];

  return matches.some((route) => {
    if (route === '/') return pathname === '/';
    return pathname === route || pathname.startsWith(`${route}/`);
  });
}

export function getActiveShellItem(pathname) {
  return allShellItems.find((item) => pathMatchesItem(pathname, item)) ?? appShellNav.primary[0];
}
